import { useState, type FormEvent } from "react";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeader from "@/components/feature/SectionHeader";

type FormStatus = "idle" | "submitting" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const FEATURES = [
  {
    icon: "ri-layout-grid-line",
    title: "丰富的影视资料",
    desc: "覆盖电影与电视剧，提供年份、地区、类型、主演等多维信息，方便快速了解作品。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维度筛选",
    desc: "按地区、年份、类型叠加筛选，并可切换最新或热门排序，快速定位想看的作品。",
  },
  {
    icon: "ri-newspaper-line",
    title: "剧情与资讯",
    desc: "整理剧情简介与相关资讯，帮助你在选择之前先了解故事与动态。",
  },
  {
    icon: "ri-search-eye-line",
    title: "快捷搜索",
    desc: "支持按片名、类型、地区、资讯关键词搜索，一步直达对应作品与报道。",
  },
];

const STEPS = [
  "在首页或顶部搜索框输入关键词，快速找到想看的影视作品或资讯。",
  "进入电影 / 电视剧分类页，使用左侧筛选栏按地区、年份、类型叠加筛选，并切换排序方式。",
  "点击任意作品卡片进入详情页，查看完整剧情简介、相关信息与同类推荐。",
  "在资讯频道按主题浏览行业动态，点击标题即可阅读完整内容。",
];

export default function About() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");
  const [formMessage, setFormMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormMessage("提交成功，感谢你的反馈，我们会尽快处理。");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");
    setFormMessage("");

    try {
      const response = await fetch("https://readdy.ai/api/form/db7mfbatp9sllopf7hr0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK") {
        setStatus("success");
        setFormMessage("提交成功，感谢你的反馈，我们会尽快处理。");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam ? "提交未通过校验，请检查后重试。" : serverMsg || "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  const inputClass =
    "w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors";

  return (
    <>
      <PageMeta
        title="关于我们-蜜桃成熟影视内容聚合平台"
        description="了解蜜桃成熟的平台定位、功能特色与使用说明，并可通过反馈表单与我们联系。"
      />

      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        <section className="mt-6 rounded-xl border border-background-200 bg-primary-50 px-6 md:px-10 py-10 md:py-14">
          <span className="text-xs font-label tracking-[0.3em] text-primary-700">ABOUT US</span>
          <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950 mt-3 mb-5">
            关于蜜桃成熟
          </h1>
          <p className="text-sm md:text-base text-foreground-700 leading-loose max-w-4xl">
            蜜桃成熟是一个综合影视内容聚合平台，专注为电影与电视剧爱好者提供丰富的影视资料、剧情介绍与行业资讯。
            我们希望把散落各处的影视信息整理成清晰直观的片单与资讯，帮助用户更轻松地浏览最新作品、了解剧情走向、
            并按自己的偏好筛选与发现好内容。平台不提供在线播放与下载服务，所有内容均聚焦于「了解与选择」。
          </p>
          <p className="text-sm md:text-base text-foreground-700 leading-loose max-w-4xl mt-4">
            本站为演示站点，所展示的影视作品、演员与资讯均为虚构示例，仅用于展示产品形态与交互体验。
          </p>
        </section>

        <section className="mt-12">
          <SectionHeader title="平台特色" subtitle="我们为用户提供的核心能力" accent />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-900 text-2xl mb-4">
                  <i className={item.icon}></i>
                </span>
                <h3 className="text-base font-medium text-foreground-950 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <SectionHeader title="使用说明" />
            <ol className="space-y-4">
              {STEPS.map((step, i) => (
                <li
                  key={i}
                  className="flex gap-4 p-4 rounded-lg border border-background-200 bg-background-50"
                >
                  <span className="w-7 h-7 shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm">
                    {i + 1}
                  </span>
                  <p className="text-sm text-foreground-700 leading-relaxed">{step}</p>
                </li>
              ))}
            </ol>
          </div>

          <div>
            <SectionHeader title="联系方式" />
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 mb-6 space-y-3 text-sm text-foreground-700">
              <p className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-mail-line"></i>
                </span>
                邮箱：hello@peachcinema.demo（演示地址）
              </p>
              <p className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-wechat-line"></i>
                </span>
                微信公众号：peach_cinema（演示）
              </p>
              <p className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-time-line"></i>
                </span>
                服务时间：每日 09:00 - 22:00
              </p>
            </div>

            <h3 className="text-base font-medium text-foreground-950 mb-4">反馈与建议</h3>
            <form
              id="contact-feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <input
                type="text"
                name="company_alt"
                className="form-honeypot-decoy"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm text-foreground-600 mb-2">
                    称呼
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm text-foreground-600 mb-2">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-topic" className="block text-sm text-foreground-600 mb-2">
                  反馈类型
                </label>
                <select id="contact-topic" name="topic" className={inputClass} defaultValue="内容建议">
                  <option value="内容建议">内容建议</option>
                  <option value="功能反馈">功能反馈</option>
                  <option value="商务合作">商务合作</option>
                  <option value="其他问题">其他问题</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm text-foreground-600 mb-2">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="欢迎告诉我们你的想法（最多 500 字）"
                  className={`${inputClass} resize-none`}
                ></textarea>
              </div>

              <div className="flex items-center gap-4 flex-wrap">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="px-6 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm whitespace-nowrap transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? "提交中..." : "提交反馈"}
                </button>
                {status === "success" && (
                  <p className="text-sm text-primary-700 flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-checkbox-circle-line"></i>
                    </span>
                    {formMessage}
                  </p>
                )}
                {status === "error" && (
                  <p className="text-sm text-accent-700 flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-error-warning-line"></i>
                    </span>
                    {formError}
                  </p>
                )}
              </div>
            </form>
          </div>
        </section>

        <section className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <h2 className="text-lg font-heading font-semibold text-foreground-950 mb-4">版权声明</h2>
          <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
            <p>
              本站所有影视作品名称、剧情介绍、演职员名单及资讯内容均为演示用途的虚构示例，与实际作品、人物及机构无关。
            </p>
            <p>
              本站不提供任何影视资源的在线播放、下载或存储服务，所有信息仅用于内容展示与产品功能演示。
            </p>
            <p>© 2026 蜜桃成熟 Peach Cinema. 演示站点，内容仅供学习与参考。</p>
          </div>
        </section>
      </div>
    </>
  );
}